import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ActionItemsProvider } from './context/ActionItemsContext';
import Layout from './Layout';
import notificationService from './services/notificationService';
import axios from 'axios';
import { initMobileAuthFixes } from './utils/mobileAuthFix';
import { captureFbclid } from './utils/fbTracking';
import logger from './utils/logger';

// Lazy-loaded page components for code splitting
const Clients = lazy(() => import('./pages/Clients'));
const Meetings = lazy(() => import('./pages/Meetings'));
const Pipeline = lazy(() => import('./pages/Pipeline'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ActionItems = lazy(() => import('./pages/ActionItems'));
const Templates = lazy(() => import('./pages/Templates'));
const Settings = lazy(() => import('./pages/Settings'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AuthConfirm = lazy(() => import('./pages/AuthConfirm'));
const OnboardingFlow = lazy(() => import('./pages/Onboarding/OnboardingFlow'));
const AskAdvicly = lazy(() => import('./pages/AskAdvicly'));
const Documents = lazy(() => import('./pages/Documents'));

// Initialize mobile auth fixes on app load
initMobileAuthFixes();

// Capture fbclid from URL for Meta CAPI attribution
captureFbclid();

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

function PrivateRoute() {
  const { isAuthenticated, isLoading, getAccessToken } = useAuth();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);
  const navigate = useNavigate();

  // Safety timeout: If still loading after 10 seconds, stop loading
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (checkingOnboarding) {
        logger.warn('⚠️ Onboarding check timeout - stopping loading state');
        setCheckingOnboarding(false);
      }
    }, 10000); // 10 seconds

    return () => clearTimeout(timeout);
  }, [checkingOnboarding]);

  useEffect(() => {
    logger.log('🔍 PrivateRoute useEffect:', { isAuthenticated, hasCheckedOnboarding, isLoading });

    // Only check onboarding status once when user first authenticates
    if (isAuthenticated && !hasCheckedOnboarding) {
      setHasCheckedOnboarding(true);

      logger.log('✅ User authenticated, checking onboarding and subscription status...');

      // Initialize notification service
      notificationService.initialize();

      // Check onboarding and subscription status
      const checkOnboardingAndSubscription = async () => {
        try {
          const token = await getAccessToken();
          logger.log('🔑 Got access token:', token ? 'Present' : 'Missing');

          // Check onboarding and subscription status in parallel
          const [onboardingResponse, subscriptionResult] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/auth/onboarding/status`, {
              headers: { Authorization: `Bearer ${token}` }
            }),
            axios.get(`${API_BASE_URL}/api/billing/subscription`, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(subError => {
              logger.warn('⚠️ Could not fetch subscription status:', subError.message);
              return null;
            })
          ]);

          logger.log('📋 Onboarding status response:', onboardingResponse.data);

          if (subscriptionResult) {
            logger.log('💳 Subscription status:', subscriptionResult.data);
            const validStatuses = ['active', 'trialing'];
            if (subscriptionResult.data && !validStatuses.includes(subscriptionResult.data.status)) {
              logger.warn('⚠️ Invalid subscription status:', subscriptionResult.data.status);
            }
          }

          setCheckingOnboarding(false);

          // Redirect to onboarding if not completed
          if (!onboardingResponse.data.onboarding_completed) {
            logger.log('🔄 Redirecting to onboarding...');
            navigate('/onboarding');
          } else {
            logger.log('✅ Onboarding completed, staying on current route');
          }
        } catch (error) {
          logger.error('❌ Error checking onboarding status:', error);
          logger.error('❌ Error details:', error.response?.data || error.message);

          // If we get a 401, the user is not properly authenticated
          if (error.response?.status === 401) {
            logger.error('❌ 401 Unauthorized - token may be invalid');
          }

          // If we get a 403 with subscription error, redirect to register
          if (error.response?.status === 403 && error.response?.data?.error?.includes('subscription')) {
            logger.log('🔄 Redirecting to register due to subscription issue...');
            navigate('/register');
          }

          setCheckingOnboarding(false);
        }
      };

      checkOnboardingAndSubscription();
    } else if (!isAuthenticated) {
      // Reset check flag when user logs out
      logger.log('👋 User not authenticated, resetting onboarding check');
      setHasCheckedOnboarding(false);
      setCheckingOnboarding(true);
    } else if (!isLoading && isAuthenticated && hasCheckedOnboarding) {
      // User is authenticated and we've already checked - stop loading
      logger.log('✅ Already checked onboarding, stopping loading state');
      setCheckingOnboarding(false);
    }
  }, [isAuthenticated, hasCheckedOnboarding, isLoading, getAccessToken, navigate]);

  logger.log('PrivateRoute: isAuthenticated:', isAuthenticated, 'isLoading:', isLoading);

  if (isLoading || checkingOnboarding) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Outlet /> : <Navigate to="/login" />;
}

function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/register" />;
}

function App() {
  return (
    <AuthProvider>
      <ActionItemsProvider>
      <Router>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        }>
        <Routes>
          {/* Root redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Public routes */}
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/confirm" element={<AuthConfirm />} />

          {/* Protected routes */}
          <Route element={<PrivateRoute />}>
            {/* Onboarding route (outside Layout) */}
            <Route path="/onboarding" element={<OnboardingFlow />} />

            {/* Main app routes (inside Layout) */}
            <Route element={<Layout />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="meetings" element={<Meetings />} />
              <Route path="clients" element={<Clients />} />
              <Route path="pipeline" element={<Pipeline />} />
              <Route path="action-items" element={<ActionItems />} />
              <Route path="templates" element={<Templates />} />
              <Route path="settings" element={<Settings />} />
              <Route path="ask-advicly" element={<AskAdvicly />} />
              <Route path="documents" element={<Documents />} />
            </Route>
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/register" />} />
        </Routes>
        </Suspense>
      </Router>
      </ActionItemsProvider>
    </AuthProvider>
  );
}

export default App;
